import { useRef, useState, type FormEvent } from 'react';
import { useLoadingState } from '../../hooks';
import { TwindenLoader } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Download, Search } from 'lucide-react';
import {
  SIGN_IN_LOG_RETENTION_DAYS,
  type SignInEventKind,
} from '@twinden/shared';
import {
  adminApi,
  ApiError,
  type LawfulAccount,
  type SignInEvent,
} from '../../lib/api';
import { appLocale } from '../../lib/format';

// Lawful requests (M65, docs/lawful-requests.md, D244): the system root looks
// an account up by handle or id, reads its sign-in log (D242, kept 180 days,
// never shown to the person) and exports it for a legal request. Every
// export is written to the audit log. A handle may name more than one
// account (its live holder and anyone deleted who had it before, D245): the
// admin then picks the one the order means.

export const KIND_LABELS: Record<SignInEventKind, string> = {
  sign_in: 'Signed in',
  qr_approve: 'Approved a QR sign-in',
  review_link: 'Review link',
  sign_out: 'Signed out',
  refresh_rejected: 'Refresh token reused',
};

const kindColor = (kind: SignInEventKind) =>
  kind === 'refresh_rejected'
    ? 'red'
    : kind === 'sign_out'
      ? 'gray'
      : kind === 'review_link'
        ? 'purple'
        : 'green';

const shortId = (id: string | null) => (id ? id.slice(0, 8) : '—');

/** The app or browser the device said it was, and its passkey name. */
const clientText = (event: SignInEvent) =>
  [event.client, event.device].filter(Boolean).join(' · ') || '—';

function KindBadge({ kind }: { kind: SignInEventKind }) {
  return (
    <Badge
      colorScheme={kindColor(kind)}
      borderRadius="full"
      px={2}
      textTransform="none"
      title={kind}
      whiteSpace="nowrap"
    >
      {KIND_LABELS[kind] ?? kind}
    </Badge>
  );
}

/** Hands the browser a file to save. */
export function saveFile(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Page {
  events: SignInEvent[];
  before: string | null;
  beforeId: string | null;
}

const EMPTY: Page = { events: [], before: null, beforeId: null };

export function LawfulRequestsTab() {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [looking, setLooking] = useState(false);
  const [looked, setLooked] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<LawfulAccount[]>([]);
  const [selected, setSelected] = useState<LawfulAccount | null>(null);
  const [page, setPage] = useState<Page>(EMPTY);
  const [loadingEvents, setLoadingEvents] = useState(false);
  // The small mark while an account's sign-ins load (M66, D250).
  const eventsWait = useLoadingState({ loading: looking || loadingEvents });
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportedAt, setExportedAt] = useState<Date | null>(null);
  // Answers to an earlier lookup or selection are ignored, so one person's
  // events never show under another's card.
  const requestNo = useRef(0);

  const fail = (title: string, err: unknown) =>
    toast({
      title,
      description:
        err instanceof ApiError ? err.message : 'Something went wrong',
      status: 'error',
    });

  const clear = () => {
    setAccounts([]);
    setSelected(null);
    setPage(EMPTY);
    setExportedAt(null);
  };

  const loadEvents = async (account: LawfulAccount, no: number) => {
    setLoadingEvents(true);
    try {
      const res = await adminApi.getSignInEvents(account.userId);
      if (no !== requestNo.current) return;
      setSelected(account);
      setPage({
        events: res.data.events,
        before: res.data.nextBefore,
        beforeId: res.data.nextBeforeId,
      });
    } catch (err) {
      if (no === requestNo.current) fail('Could not load the sign-ins', err);
    } finally {
      if (no === requestNo.current) setLoadingEvents(false);
    }
  };

  const lookUp = async (event: FormEvent) => {
    event.preventDefault();
    const q = query.trim();
    if (!q) return;
    const no = ++requestNo.current;
    clear();
    setLooked(null);
    setLooking(true);
    try {
      const res = await adminApi.lookupAccounts(q);
      if (no !== requestNo.current) return;
      setAccounts(res.data.accounts);
      setLooked(q);
      // One match: straight to its log. Several: the admin picks.
      if (res.data.accounts.length === 1) {
        await loadEvents(res.data.accounts[0]!, no);
      }
    } catch (err) {
      if (no === requestNo.current) fail('Could not look up', err);
    } finally {
      if (no === requestNo.current) setLooking(false);
    }
  };

  const select = (account: LawfulAccount) => {
    const no = ++requestNo.current;
    // The old card's Export goes dark and its events clear before the new
    // ones arrive: a failed load shows nothing under either card.
    setSelected(null);
    setPage(EMPTY);
    setExportedAt(null);
    void loadEvents(account, no);
  };

  const more = async () => {
    if (!selected || !page.before || !page.beforeId) return;
    const no = requestNo.current;
    setLoadingMore(true);
    try {
      const res = await adminApi.getSignInEvents(selected.userId, {
        before: page.before,
        beforeId: page.beforeId,
      });
      if (no !== requestNo.current) return;
      setPage((current) => ({
        events: [...current.events, ...res.data.events],
        before: res.data.nextBefore,
        beforeId: res.data.nextBeforeId,
      }));
    } catch (err) {
      fail('Could not load more', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const { blob, filename } = await adminApi.exportSignInEvents(
        selected.userId
      );
      saveFile(blob, filename);
      setExportedAt(new Date());
    } catch (err) {
      fail('Could not export', err);
    } finally {
      setExporting(false);
    }
  };

  const when = (iso: string) => new Date(iso).toLocaleString(appLocale());
  const keptUntil = (deletedAt: string) =>
    new Date(
      new Date(deletedAt).getTime() +
        SIGN_IN_LOG_RETENTION_DAYS * 24 * 3600 * 1000
    ).toLocaleDateString(appLocale());

  const several = accounts.length > 1;
  const { events } = page;

  return (
    <VStack align="stretch" spacing={5}>
      <Text color="muted" fontSize="sm">
        Sign-ins, sign-outs, QR approvals and rejected refresh tokens with the
        connecting IP address, each kept {SIGN_IN_LOG_RETENTION_DAYS} days as
        the CERT-In directions and the IT Rules 2021 require, then purged.
        Nobody sees this in the app. Use it only to answer a lawful order; every
        export is written to the audit log.
      </Text>

      <Box as="form" onSubmit={lookUp}>
        <HStack align="flex-end" flexWrap="wrap" spacing={3}>
          <FormControl maxW="420px" flex="1 1 240px">
            <FormLabel fontSize="sm">Handle or user id</FormLabel>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="@priya or a user id"
              autoComplete="off"
              bg="surface"
            />
          </FormControl>
          <Button
            type="submit"
            leftIcon={<Search size={16} />}
            isLoading={looking}
            isDisabled={!query.trim()}
          >
            Look up
          </Button>
        </HStack>
      </Box>

      {looked && !looking && accounts.length === 0 && (
        <Text color="muted">
          No account with that handle or id. A deleted account is found by the
          handle it had, for {SIGN_IN_LOG_RETENTION_DAYS} days after the
          deletion.
        </Text>
      )}

      {several && (
        <Text color="muted" fontSize="sm">
          {accounts.length} accounts have had this handle. Pick the one the
          order means.
        </Text>
      )}

      {accounts.map((account) => {
        const isSelected = selected?.userId === account.userId;
        return (
          <Box
            key={account.userId}
            bg="surface"
            borderWidth="1px"
            borderColor={isSelected && several ? 'accent' : 'line'}
            borderRadius="card"
            p={4}
            data-testid="lawful-account"
          >
            <VStack align="stretch" spacing={3}>
              <HStack
                justify="space-between"
                align="flex-start"
                flexWrap="wrap"
              >
                <Box>
                  <Text fontWeight={800} fontSize="17px">
                    {account.displayName ?? 'Deleted account'}{' '}
                    {account.handle && (
                      <Text as="span" color="muted" fontWeight={500}>
                        @{account.handle}
                      </Text>
                    )}
                  </Text>
                  <Text fontSize="xs" color="muted" wordBreak="break-all">
                    {account.userId}
                  </Text>
                </Box>
                {account.deletedAt && (
                  <Badge
                    colorScheme="red"
                    borderRadius="full"
                    px={2}
                    textTransform="none"
                  >
                    Deleted account
                  </Badge>
                )}
              </HStack>
              <Text fontSize="sm" color="ink.body">
                Account made {when(account.createdAt)}
                {account.deletedAt
                  ? ` · deleted ${when(account.deletedAt)} (record kept until ${keptUntil(account.deletedAt)})`
                  : ''}
              </Text>
              <HStack flexWrap="wrap" spacing={3}>
                {several && !isSelected && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    borderColor="line"
                    onClick={() => select(account)}
                  >
                    Show sign-ins
                  </Button>
                )}
                {isSelected && (
                  <>
                    <Button
                      size="sm"
                      leftIcon={<Download size={16} />}
                      isLoading={exporting}
                      onClick={() => void exportCsv()}
                    >
                      Export for a legal request
                    </Button>
                    <Text fontSize="xs" color="muted">
                      {exportedAt
                        ? `Exported ${exportedAt.toLocaleTimeString(appLocale())}; recorded in the audit log.`
                        : 'Downloads a CSV (user id, handle, kind, time in UTC, IP, client, device, session) and records the export in the audit log.'}
                    </Text>
                  </>
                )}
              </HStack>
            </VStack>
          </Box>
        );
      })}

      {(looking || loadingEvents) && eventsWait.loader && (
        <TwindenLoader size="inline" />
      )}

      {selected && !loadingEvents && events.length === 0 && (
        <Text color="muted">
          No sign-ins in the last {SIGN_IN_LOG_RETENTION_DAYS} days.
        </Text>
      )}

      {/* A table at desktop; one card per event on a phone (390 px). */}
      {events.length > 0 && (
        <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>What</Th>
                <Th>IP address</Th>
                <Th>Client</Th>
                <Th>Session</Th>
              </Tr>
            </Thead>
            <Tbody>
              {events.map((event) => (
                <Tr key={event.id} data-testid="sign-in-event">
                  <Td whiteSpace="nowrap" title={event.at}>
                    {when(event.at)}
                  </Td>
                  <Td>
                    <KindBadge kind={event.kind} />
                  </Td>
                  <Td fontFamily="mono" fontSize="xs">
                    {event.ip}
                  </Td>
                  <Td fontSize="xs">{clientText(event)}</Td>
                  <Td
                    fontFamily="mono"
                    fontSize="xs"
                    title={event.sessionId ?? ''}
                  >
                    {shortId(event.sessionId)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      {events.length > 0 && (
        <VStack
          display={{ base: 'flex', md: 'none' }}
          align="stretch"
          spacing={2}
        >
          {events.map((event) => (
            <Box
              key={event.id}
              data-testid="sign-in-event-card"
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              borderRadius="card"
              px={3}
              py={2}
            >
              <HStack justify="space-between" align="flex-start" spacing={2}>
                <Text fontSize="sm" title={event.at}>
                  {when(event.at)}
                </Text>
                <KindBadge kind={event.kind} />
              </HStack>
              <Text fontFamily="mono" fontSize="xs" mt={1}>
                {event.ip}
                <Text as="span" fontFamily="body" color="muted">
                  {' · session '}
                </Text>
                <Text as="span" title={event.sessionId ?? ''}>
                  {shortId(event.sessionId)}
                </Text>
              </Text>
              <Text fontSize="xs" color="muted">
                {clientText(event)}
              </Text>
            </Box>
          ))}
        </VStack>
      )}

      {page.before && (
        <Box>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            isLoading={loadingMore}
            onClick={() => void more()}
          >
            Show more
          </Button>
        </Box>
      )}
    </VStack>
  );
}
